import React from "react";
import Header from "./components/Header";
import Hero from "./components/Hero";
// Test imports removed from production bundle for performance
// import './firebase/globalTest';
// import './firebase/testTicketSystem';
// import './utils/emailjsTest';
import {
  getRedirectRule,
  shouldRedirectDomain,
  getMainDomainRedirect,
  shouldRedirectFullUrl,
  getFullUrlRedirect,
} from "./config/redirects";
import Features from "./components/Features";
import Industries from "./components/Industries";
import Services from "./components/Services";
import Process from "./components/Process";
import CTA from "./components/CTA";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import WhatsAppChat from "./components/WhatsAppChat";
import { ToastProvider } from "./components/Toast";
// import GDPRConsent from './components/GDPRConsent';
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./contexts/AuthContext";
import "./firebase/initializeBlogData";
import GoogleAnalytics from "./components/GoogleAnalytics";
import AffiliateTracker from "./components/AffiliateTracker";
// import ExitIntentPopup from './components/ExitIntentPopup';
// import { useExitIntent } from './hooks/useExitIntent';
import AuditScanner from "./components/AuditScanner/AuditScanner";
import PpcVsPfrSection from "./components/PpcVsPfrSection";
import GeoTeaserSection from "./components/GeoTeaserSection";
import SEOHead from "./components/SEOHead";

// Lazy load non-critical components
import {
  LazyAdminPanel,
  LazyAdminLogin,
  LazyBlogAdmin,
  LazyBlogPostEditor,
  LazyBlogPostDetail,
  LazyCategoryArchive,
  LazyTagArchive,
  LazyTagManager,
  LazyCategoryManager,
  LazyWhatsAppAdmin,
  LazyAffiliateManager,
  LazyConversionManager,
  LazyTicketManager,
  LazyTicketConversation,
  LazyCreateTicket,
  LazyTicketLookup,
  LazyAffiliatePortal,
  LazyBecomeAffiliate,
  LazyGlowingButtonDemo,
  LazyLawFirmsPage,
  LazyRealEstatePage,
  LazyNonprofitPage,
  LazyIndustrialPage,
  LazyHomeServicesPage,
  LazyContactPage,
  LazyShopifyDevelopmentPage,
  LazyWebsiteDevelopmentPage,
  LazyEcommercePage,
  LazyMVPDevelopmentPage,
  LazyMERNStackPage,
  LazyPriceComparisonPage,
  LazyPriceComparisonTeaser,
  LazyClientsPage,
  LazyProcessPage,
  LazyPricingPage,
  LazyBlogPage,
  LazyPortfolio,
  LazyTestimonials,
  LazyAssessmentSection,
  LazyClients,
  LazyPricing,
  LazyPricingPlans,
  LazyPasswordResetPage,
  LazyPfrServicePage,
  LazySpeedOptimizationPage,
  LazyGEOExpertPage,
  LazyHeadlessWordpressPage,
  LazyWordPressMaintenancePage,
  LazyHireWordpressDevelopersPage,
  LazyWoocommerceSubscriptionPage,
  LazyMoizBookingProPage,
  LazyKnowledgeBasePage,
  LazyMbpKnowledgeBasePage,
  LazyAffiliateMarketingEnginePage,
  LazyMoizWpCrmPage,
  LazyWhatsappIntakeAutomationPage,
  LazyWorkflowAutomationsPage,
  LazyLinkedinOutreachPage,
  LazyAiAgentTrackerPage,
  LazyEhmsDecisionStudioPage,
  LazyPrivacyPolicyPage,
  LazyTermsOfUsePage,
} from "./components/LazyComponent";
import { MAINTENANCE_CONFIG } from "./config/maintenance";
import MaintenanceMode from "./components/MaintenanceMode";
import ReadinessScannerPage from "./components/ReadinessScannerPage";

const App: React.FC = () => {
  // Exit intent hook
  // const { showPopup, closePopup } = useExitIntent();

  // Check if we need to redirect to main domain (for subdomains and full URLs)
  React.useEffect(() => {
    // Skip redirects in development environment or during react-snap pre-rendering
    const isReactSnap = navigator.userAgent === "ReactSnap";
    if (
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      isReactSnap
    ) {
      return;
    }

    // Check for full URL redirects first
    if (shouldRedirectFullUrl()) {
      const redirectUrl = getFullUrlRedirect();
      if (redirectUrl) {
        window.location.replace(redirectUrl);
        return;
      }
    }

    // Check for subdomain redirects
    if (shouldRedirectDomain()) {
      const mainDomain = getMainDomainRedirect();
      window.location.replace(mainDomain);
      return;
    }
  }, []);

  // Handle hash navigation for lazy-loaded components
  React.useEffect(() => {
    const handleHashScroll = () => {
      const hash = window.location.hash;
      if (!hash) return;

      const targetId = hash.replace("#", "");

      // Try to scroll immediately
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        return;
      }

      // If element doesn't exist yet (likely lazy-loading), poll for it
      let attempts = 0;
      const maxAttempts = 20; // 2 seconds total
      const interval = setInterval(() => {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
          clearInterval(interval);
        }
        attempts++;
        if (attempts >= maxAttempts) {
          clearInterval(interval);
        }
      }, 100);

      return () => clearInterval(interval);
    };

    // Run on mount and whenever hash changes
    handleHashScroll();
    window.addEventListener("hashchange", handleHashScroll);
    return () => window.removeEventListener("hashchange", handleHashScroll);
  }, []);

  // Check for maintenance mode first
  if (MAINTENANCE_CONFIG.enabled) {
    return <MaintenanceMode />;
  }

  // Simple routing based on current pathname
  // Check if current page is an admin page
  const isAdminPage = () => {
    const pathname = window.location.pathname.replace(/\/$/, "");
    return pathname.startsWith("/admin") || pathname === "/whatsapp-admin";
  };

  const isAffiliateContentPage = () => {
    const pathname = window.location.pathname.replace(/\/$/, "");
    return (
      pathname === "/affiliate-portal" ||
      pathname.startsWith("/affiliate-portal/") ||
      pathname === "/become-affiliate"
    );
  };

  const isKBPage = () => {
    const pathname = window.location.pathname.replace(/\/$/, "");
    return (
      pathname.startsWith("/moiz-subscription-kb") ||
      pathname === "/plugins/moiz-booking-pro/knowledgebase"
    );
  };

  const getCurrentPage = () => {
    const pathname = window.location.pathname.replace(/\/$/, ""); // Remove trailing slash

    // Check if current path needs redirect
    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    const redirectRule = !isLocalhost ? getRedirectRule(pathname) : null;
    if (redirectRule) {
      window.location.replace(redirectRule.to || "/");
      return <div>Redirecting...</div>;
    }

    // Blog routes
    if (pathname.startsWith("/blog/") && pathname !== "/blog") {
      return <LazyBlogPostDetail />;
    }

    // Taxonomy routes
    if (pathname.startsWith("/category/")) {
      return <LazyCategoryArchive />;
    }

    if (pathname.startsWith("/tag/")) {
      return <LazyTagArchive />;
    }

    if (pathname.match(/^\/admin\/blog\/edit\/(.+)$/)) {
      return (
        <ProtectedRoute>
          <LazyBlogPostEditor />
        </ProtectedRoute>
      );
    }

    // Tag and Category management routes
    if (pathname === "/admin/blog/tags/new") {
      return (
        <ProtectedRoute>
          <LazyTagManager />
        </ProtectedRoute>
      );
    }

    if (pathname === "/admin/blog/categories/new") {
      return (
        <ProtectedRoute>
          <LazyCategoryManager />
        </ProtectedRoute>
      );
    }

    // Ticket System Routes
    if (pathname.match(/^\/admin\/ticket-conversation\/(.+)$/)) {
      const ticketId = pathname.replace("/admin/ticket-conversation/", "");
      return (
        <ProtectedRoute>
          <LazyTicketConversation ticketId={ticketId} />
        </ProtectedRoute>
      );
    }

    // Affiliate dashboard route (legacy) -> use enhanced portal
    if (pathname.match(/^\/affiliate-dashboard\/(.+)$/)) {
      const affiliateId = pathname.replace("/affiliate-dashboard/", "");
      return <LazyAffiliatePortal affiliateId={affiliateId} />;
    }

    // Unique affiliate portal route
    if (pathname.match(/^\/affiliate-portal\/(.+)$/)) {
      const affiliateId = pathname.replace("/affiliate-portal/", "");
      return <LazyAffiliatePortal affiliateId={affiliateId} />;
    }

    // Password reset route
    if (pathname === "/reset-password") {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get("token");
      const tokenId = urlParams.get("id");

      if (!token || !tokenId) {
        window.location.href = "/affiliate-portal";
        return <div>Redirecting...</div>;
      }
      return <LazyPasswordResetPage tokenId={tokenId} token={token} />;
    }

    // Partner affiliate link route - show home page with affiliate tracking
    if (pathname.startsWith("/partner/")) {
      return (
        <>
          <Hero />
          <Features />
          <Industries />
          <Services />
          <Process />
          <LazyPortfolio />
          <CTA />
          <LazyAssessmentSection />
          <LazyClients />
          <LazyTestimonials />
          <LazyPricing />
        </>
      );
    }

    // Knowledge Base Route
    if (pathname.startsWith("/moiz-subscription-kb")) {
      const slug =
        pathname.replace("/moiz-subscription-kb", "").replace(/^\//, "") ||
        "introduction";
      return <LazyKnowledgeBasePage articleSlug={slug} />;
    }

    switch (pathname) {
      case "/admin":
        return (
          <ProtectedRoute>
            <LazyAdminPanel />
          </ProtectedRoute>
        );
      case "/admin/blog":
        return (
          <ProtectedRoute>
            <LazyBlogAdmin />
          </ProtectedRoute>
        );
      case "/admin/blog/new":
        return (
          <ProtectedRoute>
            <LazyBlogPostEditor />
          </ProtectedRoute>
        );
      case "/whatsapp-admin":
        return (
          <ProtectedRoute>
            <LazyWhatsAppAdmin />
          </ProtectedRoute>
        );
      case "/blog":
        return <LazyBlogPage />;
      case "/law-firm-website-design":
        return <LazyLawFirmsPage />;
      case "/real-estate-ai-representation-infrastructure":
      case "/real-estate-ai-infrastructure-representation":
      case "/real-estate-website-design":
        return <LazyRealEstatePage />;
      case "/nonprofit-website-design":
        return <LazyNonprofitPage />;
      case "/industrial-website-design":
        return <LazyIndustrialPage />;
      case "/websites-for-home-service-businesses":
        return <LazyHomeServicesPage />;
      case "/contact":
        return <LazyContactPage />;
      case "/website-design":
        window.location.replace("/website-development");
        return <div>Redirecting...</div>;
      case "/shopify-development":
        return <LazyShopifyDevelopmentPage />;
      case "/website-development":
        return <LazyWebsiteDevelopmentPage />;
      case "/ecommerce":
        return <LazyEcommercePage />;
      case "/mvp-development":
        return <LazyMVPDevelopmentPage />;
      case "/mern-stack":
        window.location.replace("/mern-stack-development");
        return <div>Redirecting...</div>;
      case "/mern-stack-development":
        return <LazyMERNStackPage />;
      case "/price-comparison-website-development":
        return <LazyPriceComparisonPage />;
      case "/clients":
        return <LazyClientsPage />;
      case "/process":
        return <LazyProcessPage />;
      case "/pricing":
        return <LazyPricingPage />;
      case "/pay-for-result":
        return <LazyPfrServicePage />;
      case "/high-performance-website":
        return <LazySpeedOptimizationPage />;
      case "/geo-expert":
        return <LazyGEOExpertPage />;
      case "/headless-wordpress":
        return <LazyHeadlessWordpressPage />;
      case "/wordpress-maintenance":
        return <LazyWordPressMaintenancePage />;
      case "/hire-wordpress-developers":
        return <LazyHireWordpressDevelopersPage />;
      case "/moiz-subscriptions-pro":
        return <LazyWoocommerceSubscriptionPage />;
      case "/moiz-booking-pro":
        return <LazyMoizBookingProPage />;
      case "/readiness-scanner":
        return <ReadinessScannerPage />;
      case "/plugins/moiz-booking-pro/knowledgebase":
        return <LazyMbpKnowledgeBasePage />;
      case "/affiliate-marketing-engine":
        return <LazyAffiliateMarketingEnginePage />;
      case "/moizwp-crm":
        return <LazyMoizWpCrmPage />;
      case "/whatsapp-intake-automation":
        return <LazyWhatsappIntakeAutomationPage />;
      case "/workflow-automations-development":
        return <LazyWorkflowAutomationsPage />;
      case "/linkedin-outreach-engagement":
        return <LazyLinkedinOutreachPage />;
      case "/industry/ehms-decision-studio":
      case "/ehms-decision-studio":
        return <LazyEhmsDecisionStudioPage />;
      case "/admin/ai-agent-tracker":
        return (
          <ProtectedRoute>
            <LazyAiAgentTrackerPage />
          </ProtectedRoute>
        );

      case "/glowing-demo":
        return <LazyGlowingButtonDemo />;
      case "/admin/affiliates":
        return (
          <ProtectedRoute>
            <LazyAffiliateManager />
          </ProtectedRoute>
        );
      case "/become-affiliate":
        return <LazyBecomeAffiliate />;
      case "/affiliate-portal":
        return <LazyAffiliatePortal />;
      case "/admin/conversions":
        return (
          <ProtectedRoute>
            <LazyConversionManager />
          </ProtectedRoute>
        );
      case "/admin/tickets":
        return (
          <ProtectedRoute>
            <LazyTicketManager />
          </ProtectedRoute>
        );
      case "/create-ticket":
        return <LazyCreateTicket />;
      case "/ticket-lookup":
        return <LazyTicketLookup />;
      case "/sitemap":
      case "/sitemap.xml":
        window.location.replace("/sitemap.xml");
        return <div>Redirecting...</div>;
      case "/privacy-policy":
      case "/privacy":
        return <LazyPrivacyPolicyPage />;
      case "/terms-of-use":
      case "/terms":
      case "/terms-and-conditions":
        return <LazyTermsOfUsePage />;
      case "":
      case "/":
        return (
          <>
            <SEOHead />
            <Hero />
            <AuditScanner />
            <Features />
            <Industries />
            <Services />
            <GeoTeaserSection />
            <LazyPriceComparisonTeaser />
            <Process />
            <LazyPortfolio />
            <CTA />
            <PpcVsPfrSection />
            <LazyClients />
            <LazyTestimonials />
            <LazyPricing />
            <LazyPricingPlans />
            <LazyAssessmentSection />
          </>
        );
      case "/admin/login":
        return (
          <LazyAdminLogin onLogin={() => (window.location.href = "/admin")} />
        );
      default:
        return (
          <div className="min-h-[60vh] flex items-center justify-center pt-32 pb-16 px-4">
            <SEOHead title="404 - Page Not Found | MoizWP" noIndex={true} />
            <div className="text-center">
              <h1 className="text-6xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                404
              </h1>
              <h2 className="text-2xl font-semibold text-gray-700 mb-6 font-outfit uppercase tracking-widest">
                Page Not Found
              </h2>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                Oops! The page you're searching for seems to have vanished or
                moved to another universe.
              </p>
              <a
                href="/"
                className="inline-flex items-center px-8 py-3.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-all duration-300 font-semibold shadow-[0_10px_20px_rgba(37,99,235,0.2)] hover:shadow-[0_15px_30px_rgba(37,99,235,0.3)] hover:-translate-y-1"
              >
                Go Back Home
              </a>
            </div>
          </div>
        );
    }
  };

  return (
    <AuthProvider>
      <ToastProvider>
        <AffiliateTracker>
          <div className="App">
            <GoogleAnalytics measurementId="G-XLMPWHRHEB" />
            {!isAdminPage() && !isKBPage() && <Header />}
            <main className={isAffiliateContentPage() ? "mt-0" : ""}>
              {getCurrentPage()}
            </main>
            {!isAdminPage() && !isKBPage() && <Footer />}
            {!isAdminPage() && !isKBPage() && (
              <>
                <WhatsAppChat autoMessage="Hi! I'm interested in your web development services. Can you help me get started?" />
                <ScrollToTop />
              </>
            )}
            {/* <GDPRConsent
              onAccept={() => { }}
              onDecline={() => { }}
              onCustomize={() => { }}
            /> */}
            {/* Exit Intent Popup */}
            {/* <ExitIntentPopup
              isVisible={showPopup}
              onClose={closePopup}
            /> */}
          </div>
        </AffiliateTracker>
      </ToastProvider>
    </AuthProvider>
  );
};

export default App;
